/** Message-key prefix of the feedback texts, shared with the JSF metadata templates. */
const TRANSLATION_BASE = 'cs.sys.validation.resourceType.typeTexts.document.signedPdf.feedback';

/** Property keys the signature state is derived from. */
const PROPERTY_SIGNING_TYPE = 'signingType';
const PROPERTY_REPORT_TYPE = 'signatureValidation.reportType';
const PROPERTY_CPDF_STATUS = 'signatureValidation.cpdfStatus';
const PROPERTY_SUBINDICATION = 'signatureValidation.subindication';
const PROPERTY_SIGNATURE_TIME = 'signatureValidation.signatureTime';
const PROPERTY_CERTIFICATE_CHAIN = 'signatureValidation.certificateChain';

/**
 * The signature state of a PDF: the report type of the validation for a signed document,
 * the signing type otherwise.
 */
export type SignedPdfState = 'valid' | 'validCpdf' | 'revoked' | 'indeterminate' | 'invalid' | 'unsigned' | 'unreadable';

/** Everything needed to present the signature state of a PDF to the user. */
export interface SignedPdfFeedback {
    state: SignedPdfState;

    /** Style classes of the feedback block, mirroring the ones of the JSF templates. */
    styleClass: string;

    /** Title of the status icon, also used as its alternative text. */
    iconTitle: string;

    /** The feedback itself. Contains markup, so it has to be rendered as HTML. */
    text: string;
}

/**
 * Derives the feedback presented for the {@code signedPdfResource} properties of a file, or
 * {@code null} when they describe no state this component knows.
 *
 * @param properties the untranslated property values of the category
 * @param translate resolves a message key — {@code HisUiService.translate} bound to its service
 */
export function signedPdfFeedback(
    properties: Map<string, string>,
    translate: ( key: string, ...params: string[] ) => string
): SignedPdfFeedback | null {
    const state = signedPdfState( properties );
    if ( state == null ) {
        return null;
    }

    return {
        state,
        styleClass: styleClassOf( state ),
        iconTitle: translate( `${TRANSLATION_BASE}.${state}.icon` ),
        text: feedbackTextOf( state, properties, translate ),
    };
}

function signedPdfState( properties: Map<string, string> ): SignedPdfState | null {
    switch ( properties.get( PROPERTY_SIGNING_TYPE ) ) {
        case 'signed':
            return reportTypeState( properties.get( PROPERTY_REPORT_TYPE ) );
        case 'unsigned':
            return 'unsigned';
        case 'unreadable':
            return 'unreadable';
        default:
            return null;
    }
}

function reportTypeState( reportType: string | undefined ): SignedPdfState | null {
    switch ( reportType ) {
        case 'valid':
        case 'validCpdf':
        case 'revoked':
        case 'indeterminate':
        case 'invalid':
            return reportType;
        default:
            return null;
    }
}

function styleClassOf( state: SignedPdfState ): string {
    switch ( state ) {
        case 'unsigned':
        case 'unreadable':
            return `signedPdfResource-${state}`;
        default:
            return `signedPdfResource-signed signedPdfResource-${state}`;
    }
}

function feedbackTextOf(
    state: SignedPdfState,
    properties: Map<string, string>,
    translate: ( key: string, ...params: string[] ) => string
): string {
    switch ( state ) {
        case 'valid':
            return translate(
                `${TRANSLATION_BASE}.valid.feedback`,
                properties.get( PROPERTY_SIGNATURE_TIME ) ?? '',
                properties.get( PROPERTY_CERTIFICATE_CHAIN ) ?? ''
            );
        case 'validCpdf':
            return translate(
                `${TRANSLATION_BASE}.validCpdf.feedback`,
                properties.get( PROPERTY_SIGNATURE_TIME ) ?? '',
                properties.get( PROPERTY_CERTIFICATE_CHAIN ) ?? ''
            );
        case 'revoked':
            return translate(
                `${TRANSLATION_BASE}.revoked.feedback`,
                properties.get( PROPERTY_SIGNATURE_TIME ) ?? '',
                properties.get( PROPERTY_CERTIFICATE_CHAIN ) ?? '',
                properties.get( PROPERTY_CPDF_STATUS ) ?? ''
            );
        case 'indeterminate':
        case 'invalid':
            // The reason the validation could not confirm the signature is part of the key.
            return translate( `${TRANSLATION_BASE}.${state}.${properties.get( PROPERTY_SUBINDICATION )}` );
        default:
            return translate( `${TRANSLATION_BASE}.${state}.feedback` );
    }
}
